import { ref } from 'vue'
import { defineStore } from 'pinia'

export interface AllowedAction {
  actionKey: string
  resourceType?: string
  resourceId?: string
  expiresAt?: string
}

export interface TemporaryGrant {
  id: string
  actionKey: string
  resourceType: string
  resourceId?: string
  expiresAt: string
  oneUse: boolean
}

export interface AuthorizationScope {
  resourceType?: string
  resourceId?: string
}

function isUnexpired(value: string | undefined, now: number): boolean {
  if (value === undefined) return true
  const expiresAt = Date.parse(value)
  return Number.isFinite(expiresAt) && expiresAt > now
}

function scopeMatches(candidate: AuthorizationScope, requested: AuthorizationScope): boolean {
  if (requested.resourceType !== undefined && candidate.resourceType !== requested.resourceType) return false
  if (requested.resourceId !== undefined) {
    if (requested.resourceType === undefined) return false
    return candidate.resourceId === undefined || candidate.resourceId === requested.resourceId
  }
  return candidate.resourceId === undefined
}

export const useSessionStore = defineStore('session', () => {
  const environment = ref('未连接')
  const dataScope = ref('未授权')
  const permissionEpoch = ref(0)
  const temporaryGrantExpiresAt = ref<string | null>(null)
  const allowedActions = ref<AllowedAction[]>([])
  const temporaryGrants = ref<TemporaryGrant[]>([])

  function isAllowed(action: string, scope: AuthorizationScope = {}, now = Date.now()): boolean {
    const actionMatches = allowedActions.value.filter((item) => item.actionKey === action && scopeMatches(item, scope))
    if (!actionMatches.some((item) => isUnexpired(item.expiresAt, now))) return false

    const actionGrants = temporaryGrants.value.filter((grant) => grant.actionKey === action)
    return actionGrants.length === 0
      || actionGrants.some((grant) => scopeMatches(grant, scope) && isUnexpired(grant.expiresAt, now))
  }

  function clearSessionState() {
    environment.value = '未连接'
    dataScope.value = '未授权'
    permissionEpoch.value = 0
    allowedActions.value = []
    temporaryGrants.value = []
    temporaryGrantExpiresAt.value = null
  }

  function clearSensitiveState() {
    clearSessionState()
    if (typeof document !== 'undefined') {
      document.querySelectorAll('meta[name="csrf-token"]').forEach((element) => element.remove())
    }
  }

  return {
    environment,
    dataScope,
    permissionEpoch,
    temporaryGrantExpiresAt,
    allowedActions,
    temporaryGrants,
    isAllowed,
    clearSessionState,
    clearSensitiveState,
  }
})
